import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { publicUrl } from "@/lib/storage";
import { toast } from "sonner";

type ChangeRow = {
  id: string;
  actor_user_id: string | null;
  table_name: string;
  row_id: string;
  action: string;
  diff: any;
  status: string;
  created_at: string;
};

type Cause = { id: string; name: string; published: boolean; athlete_id: string | null };
type Product = { id: string; brand_name: string; product_name: string; published: boolean; athlete_id: string | null };
type AthleteMini = { id: string; name: string; slug: string; image_path: string | null };

// Fields we hide from the diff — either noisy, system-managed, or already reflected in another field.
const HIDDEN_FIELDS = new Set(["updated_at", "created_at", "id", "owner_user_id", "sort_order"]);

// Fields that store storage paths we can render as image previews.
const IMAGE_FIELDS = new Set(["image_path", "hero_image_path", "logo_path", "cover_image_path"]);
const IMAGE_ARRAY_FIELDS = new Set(["gallery_paths", "image_paths"]);

const STATUSES = ["pending", "acknowledged", "reverted", "all"] as const;
type StatusFilter = (typeof STATUSES)[number];

const formatFieldName = (k: string) =>
  k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ValueCell = ({ field, value }: { field: string; value: any }) => {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground italic">—</span>;
  }
  if (IMAGE_FIELDS.has(field) && typeof value === "string") {
    const url = publicUrl(value);
    return url ? (
      <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" />
    ) : (
      <span className="text-xs text-muted-foreground">{value}</span>
    );
  }
  if (IMAGE_ARRAY_FIELDS.has(field) && Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-1">
        {value.slice(0, 6).map((p: string, i: number) => {
          const url = publicUrl(p);
          return url ? (
            <img key={i} src={url} alt="" className="w-10 h-10 rounded object-cover border border-border" />
          ) : null;
        })}
        {value.length > 6 && <span className="text-xs text-muted-foreground self-center">+{value.length - 6}</span>}
      </div>
    );
  }
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground italic">empty</span>;
    return <span className="text-xs">{value.join(", ")}</span>;
  }
  if (typeof value === "object") {
    return <pre className="text-[11px] bg-muted/40 p-2 rounded max-w-xs overflow-x-auto">{JSON.stringify(value, null, 2)}</pre>;
  }
  const str = String(value);
  if (str.length > 140) {
    return (
      <details>
        <summary className="cursor-pointer text-sm">{str.slice(0, 140)}…</summary>
        <p className="text-sm mt-1 whitespace-pre-wrap">{str}</p>
      </details>
    );
  }
  return <span className="text-sm whitespace-pre-wrap break-words">{str}</span>;
};

const DiffTable = ({ diff }: { diff: any }) => {
  const rows = Object.entries(diff || {}).filter(([k, v]: [string, any]) => {
    if (HIDDEN_FIELDS.has(k)) return false;
    if (!v || typeof v !== "object") return false;
    return JSON.stringify(v.old) !== JSON.stringify(v.new);
  });
  if (rows.length === 0) return <p className="text-xs text-muted-foreground">No visible field changes.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <th className="py-2 pr-4 font-semibold w-1/4">Field</th>
            <th className="py-2 pr-4 font-semibold w-3/8">Before</th>
            <th className="py-2 pr-4 font-semibold w-3/8">After</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([k, v]: [string, any]) => (
            <tr key={k} className="border-b border-border/50 align-top">
              <td className="py-3 pr-4 font-semibold text-xs uppercase tracking-wider">{formatFieldName(k)}</td>
              <td className="py-3 pr-4 bg-destructive/5 rounded"><ValueCell field={k} value={v.old} /></td>
              <td className="py-3 pr-4 bg-primary/5 rounded"><ValueCell field={k} value={v.new} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminReview = () => {
  const [pendingCauses, setPendingCauses] = useState<Cause[]>([]);
  const [pendingProducts, setPendingProducts] = useState<Product[]>([]);
  const [changes, setChanges] = useState<ChangeRow[]>([]);
  const [athletesById, setAthletesById] = useState<Record<string, AthleteMini>>({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");

  const load = async () => {
    setLoading(true);
    const [c, p, l] = await Promise.all([
      supabase.from("causes").select("id, name, published, athlete_id").eq("published", false).order("created_at", { ascending: false }),
      supabase.from("brand_products").select("id, brand_name, product_name, published, athlete_id").eq("published", false).order("created_at", { ascending: false }),
      supabase.from("change_log").select("*").order("created_at", { ascending: false }).limit(100),
    ]);
    const changeRows = ((l.data as any) ?? []) as ChangeRow[];
    setPendingCauses((c.data as any) ?? []);
    setPendingProducts((p.data as any) ?? []);
    setChanges(changeRows);

    const athleteIds = Array.from(
      new Set(changeRows.filter((r) => r.table_name === "athletes").map((r) => r.row_id))
    );
    if (athleteIds.length) {
      const { data } = await supabase.from("athletes").select("id, name, slug, image_path").in("id", athleteIds);
      const map: Record<string, AthleteMini> = {};
      ((data as any) ?? []).forEach((a: AthleteMini) => (map[a.id] = a));
      setAthletesById(map);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const publishCause = async (id: string) => {
    const { error } = await supabase.from("causes").update({ published: true }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Cause published");
    load();
  };
  const publishProduct = async (id: string) => {
    const { error } = await supabase.from("brand_products").update({ published: true }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Product published");
    load();
  };

  const revertChange = async (c: ChangeRow) => {
    if (c.table_name !== "athletes" || c.action !== "update") {
      if (c.table_name === "causes") await supabase.from("causes").update({ published: false }).eq("id", c.row_id);
      if (c.table_name === "brand_products") await supabase.from("brand_products").update({ published: false }).eq("id", c.row_id);
      await supabase.from("change_log").update({ status: "reverted" }).eq("id", c.id);
      load();
      return;
    }
    const old: any = {};
    Object.entries(c.diff || {}).forEach(([k, v]: [string, any]) => {
      if (HIDDEN_FIELDS.has(k)) return;
      old[k] = v?.old;
    });
    if (!Object.keys(old).length) return toast.error("Nothing to revert");
    const { error } = await supabase.from("athletes").update(old).eq("id", c.row_id);
    if (error) return toast.error(error.message);
    await supabase.from("change_log").update({ status: "reverted" }).eq("id", c.id);
    toast.success("Reverted");
    load();
  };

  const acknowledge = async (id: string) => {
    await supabase.from("change_log").update({ status: "acknowledged" }).eq("id", id);
    load();
  };

  // Group athlete edits by athlete for a nicer summary card
  const athleteEdits = useMemo(() => {
    const filtered = changes.filter((c) => c.table_name === "athletes");
    return filtered.filter((c) => statusFilter === "all" || c.status === statusFilter || (statusFilter === "pending" && c.status === "live"));
  }, [changes, statusFilter]);

  const otherChanges = useMemo(
    () => changes.filter((c) => c.table_name !== "athletes"),
    [changes]
  );

  const pendingCount = changes.filter((c) => c.table_name === "athletes" && c.status === "live").length;

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl mb-6">Review Queue</h1>
      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-10">
          <section>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="font-display text-2xl">
                Athlete Edits
                {pendingCount > 0 && (
                  <span className="ml-3 text-sm bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                    {pendingCount} pending
                  </span>
                )}
              </h2>
              <div className="flex gap-1 text-xs">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full font-semibold uppercase tracking-wider ${
                      statusFilter === s ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/70"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-4">
              {athleteEdits.map((c) => {
                const a = athletesById[c.row_id];
                const img = a?.image_path ? publicUrl(a.image_path) : null;
                return (
                  <div key={c.id} className="bg-card rounded-2xl p-5 shadow-card">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                          {img && <img src={img} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div>
                          <p className="font-display text-lg leading-tight">{a?.name ?? "Unknown athlete"}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.action} · {new Date(c.created_at).toLocaleString()}
                          </p>
                        </div>
                        <span className={`ml-2 text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold ${
                          c.status === "live" ? "bg-primary/15 text-primary" :
                          c.status === "reverted" ? "bg-destructive/15 text-destructive" :
                          "bg-muted text-muted-foreground"
                        }`}>{c.status === "live" ? "pending" : c.status}</span>
                      </div>
                      <div className="flex gap-2">
                        {a?.slug && (
                          <Link
                            to={`/athlete/${a.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 rounded-full bg-muted text-xs font-semibold hover:bg-muted/70"
                          >
                            Open profile ↗
                          </Link>
                        )}
                        {c.status === "live" && (
                          <>
                            <button onClick={() => acknowledge(c.id)} className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">Approve</button>
                            <button onClick={() => revertChange(c)} className="px-3 py-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-semibold">Revert</button>
                          </>
                        )}
                      </div>
                    </div>
                    <DiffTable diff={c.diff} />
                  </div>
                );
              })}
              {athleteEdits.length === 0 && (
                <p className="text-sm text-muted-foreground">No {statusFilter === "all" ? "" : statusFilter} athlete edits.</p>
              )}
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl mb-3">Pending causes ({pendingCauses.length})</h2>
            <div className="grid gap-2">
              {pendingCauses.map((c) => (
                <div key={c.id} className="bg-card rounded-2xl p-4 flex items-center justify-between shadow-card">
                  <p className="font-semibold">{c.name}</p>
                  <button onClick={() => publishCause(c.id)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Publish</button>
                </div>
              ))}
              {pendingCauses.length === 0 && <p className="text-sm text-muted-foreground">None pending.</p>}
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl mb-3">Pending products ({pendingProducts.length})</h2>
            <div className="grid gap-2">
              {pendingProducts.map((p) => (
                <div key={p.id} className="bg-card rounded-2xl p-4 flex items-center justify-between shadow-card">
                  <p className="font-semibold">{p.brand_name} — {p.product_name}</p>
                  <button onClick={() => publishProduct(p.id)} className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold">Publish</button>
                </div>
              ))}
              {pendingProducts.length === 0 && <p className="text-sm text-muted-foreground">None pending.</p>}
            </div>
          </section>

          {otherChanges.length > 0 && (
            <section>
              <h2 className="font-display text-xl mb-3">Other recent activity</h2>
              <div className="grid gap-2">
                {otherChanges.slice(0, 20).map((c) => (
                  <div key={c.id} className="bg-card rounded-2xl p-4 shadow-card">
                    <p className="text-sm font-semibold">
                      {c.action.toUpperCase()} on {c.table_name}
                      <span className="ml-2 text-[10px] uppercase px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{c.status}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReview;
